import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Article, Contrat } from '@troc/shared'
import {
  centimesDepuisSaisie,
  codeFamille,
  decomposerVente,
  eur,
  FAMILLES,
  RESUME_BAREME,
  tauxEnPourcent,
} from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { PastilleContrat } from '../composants/Pastille'
import { imprimerContrat, imprimerEtiquettes } from '../impression/documents'
import { useToast } from '../etat/Toast'
import { useEstVisible, useReveil } from '../composants/Volets'

interface Resume {
  id: number
  num: string
  creeLe: string
  deposantNom: string
  nbArticles: number
  totalCentimes: number
  statut: 'actif' | 'echu' | 'solde'
}

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' })
const FORMAT_DATE_LONGUE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' })

export function Contrats() {
  /**
   * Le contrat choisi se lit dans l'adresse, pas dans `useParams`.
   *
   * Cet écran affiche la liste et le contrat sélectionné ensemble, et reste
   * monté quand on le quitte : il n'est donc pas rendu par une route `:num`,
   * et `useParams` ne lui renverrait jamais rien.
   */
  const { pathname } = useLocation()
  const prefixe = '/contrats/'
  const num = pathname.startsWith(prefixe)
    ? decodeURIComponent(pathname.slice(prefixe.length))
    : undefined
  const naviguer = useNavigate()
  const { signaler, alerter } = useToast()
  const [resumes, setResumes] = useState<Resume[]>([])
  const [total, setTotal] = useState(0)
  const [suiteEnCours, setSuiteEnCours] = useState(false)
  /** Sentinelle de bas de liste : la voir, c'est charger les contrats plus anciens. */
  const sentinelle = useRef<HTMLDivElement>(null)
  const [contrat, setContrat] = useState<Contrat | null>(null)
  // Mode correction : le contrat reste lisible, mais chaque ligne s'ouvre.
  const [correction, setCorrection] = useState(false)

  const [rafraichir, setRafraichir] = useState(0)
  useReveil('/contrats', () => setRafraichir((n) => n + 1))
  // L'écran reste monté quand on le quitte : sans cette garde, sa sélection
  // automatique repartirait des coulisses et ramènerait l'utilisateur ici.
  const visible = useEstVisible('/contrats')

  useEffect(() => {
    api<{ contrats: Resume[]; total: number }>('/contrats')
      .then((r) => {
        setResumes(r.contrats)
        setTotal(r.total)
        if (visible && !num && r.contrats[0]) {
          naviguer(`/contrats/${r.contrats[0].num}`, { replace: true })
        }
      })
      .catch(() => undefined)
  }, [num, naviguer, rafraichir, visible])

  /**
   * Les contrats plus anciens, à mesure qu'on descend.
   *
   * Le magasin en compte 36 506. La liste part des plus récents : sans cette
   * suite, elle s'arrêtait deux cents contrats en arrière — à la mi-juin — sans
   * rien indiquer.
   */
  const chargerSuite = useCallback(() => {
    if (suiteEnCours || resumes.length === 0 || resumes.length >= total) return
    setSuiteEnCours(true)
    api<{ contrats: Resume[] }>(`/contrats?depuis=${resumes.length}`)
      .then((r) => setResumes((actuels) => [...actuels, ...r.contrats]))
      .catch(() => undefined)
      .finally(() => setSuiteEnCours(false))
  }, [resumes.length, total, suiteEnCours])

  useEffect(() => {
    const cible = sentinelle.current
    if (!cible) return
    const observateur = new IntersectionObserver(
      (entrees) => {
        if (entrees.some((e) => e.isIntersecting)) chargerSuite()
      },
      { rootMargin: '400px' },
    )
    observateur.observe(cible)
    return () => observateur.disconnect()
  }, [chargerSuite])

  useEffect(() => {
    if (!num) return
    setContrat(null)
    setCorrection(false)
    api<{ contrat: Contrat }>(`/contrats/${encodeURIComponent(num)}`)
      .then((r) => setContrat(r.contrat))
      .catch(() => undefined)
  }, [num])

  /** Toute correction renvoie le contrat entier : on remplace, sans recharger. */
  const appliquer = async (
    chemin: string,
    options: { methode: 'PUT' | 'POST' | 'DELETE'; corps?: unknown },
    message: string,
  ) => {
    if (!num) return
    try {
      const reponse = await api<{ contrat: Contrat }>(
        `/contrats/${encodeURIComponent(num)}${chemin}`,
        options,
      )
      setContrat(reponse.contrat)
      signaler(message)
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Modification impossible.')
    }
  }

  return (
    <Ecran
      titre="Contrats & factures"
      sousTitre={
        total > resumes.length
          ? `${resumes.length} contrats chargés sur ${total} — la liste remonte le temps en descendant`
          : `${resumes.length} contrats enregistrés`
      }
      outils={
        // L'appli tourne en plein écran (PWA) : pas de bouton « précédent » du
        // navigateur. Arrivé ici depuis une fiche client ou un article, il n'y
        // avait auparavant aucun moyen de revenir sans tout retaper.
        window.history.length > 1 ? (
          <button
            type="button"
            className="btn btn-secondaire"
            style={{ marginLeft: 'auto' }}
            onClick={() => naviguer(-1)}
          >
            ← Retour
          </button>
        ) : undefined
      }
    >
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <div className="liste-contrats">
          {resumes.map((resume) => (
            <button
              key={resume.id}
              type="button"
              className="contrat-item"
              aria-pressed={resume.num === num}
              onClick={() => naviguer(`/contrats/${resume.num}`)}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  width: '100%',
                }}
              >
                <span className="titre" style={{ fontSize: 14 }}>
                  {resume.num}
                </span>
                <span className="sourdine" style={{ fontSize: 11 }}>
                  {FORMAT_DATE.format(new Date(resume.creeLe))}
                </span>
              </div>
              <div style={{ fontSize: 13 }}>{resume.deposantNom}</div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <span className="sourdine" style={{ fontSize: 11 }}>
                  {resume.nbArticles} pièce{resume.nbArticles > 1 ? 's' : ''} ·{' '}
                  {eur(resume.totalCentimes)}
                </span>
                <PastilleContrat statut={resume.statut} />
              </div>
            </button>
          ))}
          {resumes.length === 0 && <div className="vide">Aucun contrat.</div>}
          <div ref={sentinelle} />
          {resumes.length > 0 && resumes.length < total && (
            <div className="vide sourdine">
              {suiteEnCours ? 'Chargement…' : `${resumes.length} sur ${total}`}
            </div>
          )}
        </div>

        <div
          className="zone-defilante"
          style={{ background: 'var(--color-neutral-100)', padding: '28px 32px' }}
        >
          {!contrat && <div className="vide">Sélectionnez un contrat.</div>}
          {contrat && (
            <>
              <div className="feuille">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    borderBottom: '2px solid var(--color-divider)',
                    paddingBottom: 14,
                  }}
                >
                  <div>
                    <div className="titre" style={{ fontSize: 20 }}>
                      TROC EN STOCK
                    </div>
                    <div className="sourdine" style={{ fontSize: 11 }}>
                      Dépôt-vente · B-6780 Messancy
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="ref">CONTRAT DE DÉPÔT</div>
                    <div className="titre" style={{ fontSize: 18 }}>
                      {contrat.num}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 16,
                    padding: '16px 0',
                    borderBottom: '1px solid var(--color-divider)',
                    fontSize: 13,
                  }}
                >
                  <div>
                    <div className="etiquette">Déposant</div>
                    {contrat.deposant.nom} · {contrat.deposant.ref}
                    <br />
                    {contrat.deposant.tel}
                  </div>
                  <div>
                    <div className="etiquette">Période</div>
                    Dépôt du {FORMAT_DATE_LONGUE.format(new Date(contrat.creeLe))}
                    <br />
                    Démarque jusqu’au {FORMAT_DATE_LONGUE.format(new Date(contrat.finDemarque))}
                  </div>
                </div>

                <table className="tableau" style={{ margin: '8px 0 16px' }}>
                  <thead>
                    <tr>
                      <th>N°</th>
                      <th>Désignation</th>
                      <th>Famille</th>
                      <th className="a-droite">Qté</th>
                      <th className="a-droite">Prix</th>
                      <th className="a-droite">Comm.</th>
                      <th className="a-droite">Net</th>
                      <th>Statut</th>
                      {correction && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {contrat.articles.map((article) =>
                      correction ? (
                        <LigneCorrigeable
                          key={article.id}
                          article={article}
                          onEnregistrer={(champs) =>
                            appliquer(
                              `/articles/${article.id}`,
                              { methode: 'PUT', corps: champs },
                              `${article.ref} corrigé.`,
                            )
                          }
                          onRetirer={() =>
                            appliquer(
                              `/articles/${article.id}`,
                              { methode: 'DELETE' },
                              `${article.ref} retiré du contrat.`,
                            )
                          }
                        />
                      ) : (
                        <LigneLecture key={article.id} article={article} />
                      ),
                    )}
                  </tbody>
                </table>

                {correction && (
                  <AjoutArticle
                    onAjouter={(article) =>
                      appliquer(
                        '/articles',
                        { methode: 'POST', corps: article },
                        'Article ajouté au contrat.',
                      )
                    }
                  />
                )}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '2px solid var(--color-divider)',
                    paddingTop: 12,
                    alignItems: 'baseline',
                  }}
                >
                  <span className="titre" style={{ fontSize: 14 }}>
                    TOTAL DÉPOSÉ
                  </span>
                  <span className="chiffre" style={{ fontSize: 32, color: 'var(--color-accent)' }}>
                    {eur(contrat.totalCentimes)}
                  </span>
                </div>

                <p className="sourdine" style={{ fontSize: 11, margin: '16px 0 0' }}>
                  Dépôt gratuit. {RESUME_BAREME} Contrat d’un mois : au-delà, l’article non vendu
                  peut être récupéré sans frais ou rester en rayon, son prix baissant alors chaque
                  mois — −10 %, puis −20 %, −40 %, −60 % et −80 % au plus bas. Les articles doivent
                  être propres et en état de fonctionnement.
                </p>

                <div style={{ display: 'flex', gap: 40, marginTop: 22, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <div className="etiquette">Signature déposant</div>
                    {contrat.signatureSvg ? (
                      <svg
                        viewBox="0 0 360 200"
                        style={{
                          width: '100%',
                          height: 56,
                          borderBottom: '1px solid var(--color-divider)',
                        }}
                        dangerouslySetInnerHTML={{ __html: contrat.signatureSvg }}
                        fill="none"
                        stroke="var(--color-text)"
                        strokeWidth={2.4}
                      />
                    ) : (
                      <div
                        style={{
                          height: 56,
                          borderBottom: '1px solid var(--color-divider)',
                          display: 'flex',
                          alignItems: 'flex-end',
                          fontSize: 12,
                          color: 'var(--color-neutral-600)',
                        }}
                      >
                        Non signé
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="etiquette">Pour la boutique</div>
                    <div
                      style={{
                        height: 56,
                        borderBottom: '1px solid var(--color-divider)',
                        display: 'flex',
                        alignItems: 'flex-end',
                        fontSize: 13,
                        paddingBottom: 6,
                      }}
                    >
                      {contrat.vendeurNom}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={correction ? 'btn btn-primaire btn-large' : 'btn btn-secondaire btn-large'}
                  onClick={() => setCorrection((c) => !c)}
                >
                  {correction ? 'Terminer la correction' : 'Corriger le contrat'}
                </button>
                <button
                  type="button"
                  className="btn btn-primaire btn-large"
                  onClick={() => imprimerContrat(contrat)}
                >
                  Imprimer le contrat
                </button>
                <button
                  type="button"
                  className="btn btn-secondaire btn-large"
                  onClick={() => {
                    imprimerEtiquettes(contrat.articles)
                    signaler(`${contrat.articles.length} étiquettes envoyées à l’imprimante.`)
                  }}
                >
                  Réimprimer les étiquettes
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Ecran>
  )
}

/** Une ligne du contrat, telle qu'elle s'imprime. */
function LigneLecture({ article }: { article: Article }) {
  const part = decomposerVente(
    article.prixInitialCentimes,
    article.tauxCommission,
    article.deposantGroupeCommission,
  )
  return (
    <tr>
      <td className="ref">{article.ref}</td>
      <td>{article.titre}</td>
      <td className="sourdine" title={article.categorie}>
        {codeFamille(article.categorie)}
      </td>
      <td className="a-droite chiffre">{article.quantite}</td>
      <td className="a-droite">{eur(part.prixCentimes)}</td>
      <td className="a-droite">{tauxEnPourcent(part.taux)}</td>
      <td className="a-droite chiffre">{eur(part.netDeposantCentimes)}</td>
      <td>
        <span className={`pastille pastille-${article.statut}`}>
          {article.statut === 'vendu' ? 'Vendu' : 'En dépôt'}
        </span>
      </td>
    </tr>
  )
}

/**
 * La même ligne, ouverte à la correction.
 *
 * Un exemplaire déjà vendu verrouille le prix : la commission a été calculée
 * dessus et le déposant crédité. La désignation, elle, reste corrigible — une
 * faute de frappe n'a jamais changé un montant.
 */
function LigneCorrigeable({
  article,
  onEnregistrer,
  onRetirer,
}: {
  article: Article
  onEnregistrer: (champs: Record<string, unknown>) => Promise<void>
  onRetirer: () => Promise<void>
}) {
  const [titre, setTitre] = useState(article.titre)
  const [categorie, setCategorie] = useState(article.categorie)
  const [prix, setPrix] = useState(String(Math.round(article.prixInitialCentimes / 100)))
  const [quantite, setQuantite] = useState(String(article.quantite))
  const [envoi, setEnvoi] = useState(false)

  const vendus = article.quantite - article.quantiteRestante
  const prixVerrouille = vendus > 0
  const centimes = centimesDepuisSaisie(prix)
  const nombre = Number.parseInt(quantite, 10)

  const modifie =
    titre !== article.titre ||
    categorie !== article.categorie ||
    nombre !== article.quantite ||
    (!prixVerrouille && centimes !== article.prixInitialCentimes)

  const enregistrer = async () => {
    if (envoi || !modifie) return
    setEnvoi(true)
    const champs: Record<string, unknown> = {}
    if (titre !== article.titre) champs.titre = titre
    if (categorie !== article.categorie) champs.categorie = categorie
    if (nombre !== article.quantite) champs.quantite = nombre
    if (!prixVerrouille && centimes !== article.prixInitialCentimes) champs.prixCentimes = centimes
    await onEnregistrer(champs)
    setEnvoi(false)
  }

  return (
    <tr className="ligne-corrigee">
      <td className="ref">{article.ref}</td>
      <td>
        <input
          className="saisie saisie-compacte"
          style={{ maxWidth: 260 }}
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
        />
      </td>
      <td>
        <select
          className="saisie saisie-compacte"
          style={{ maxWidth: 130 }}
          value={categorie}
          onChange={(e) => setCategorie(e.target.value)}
        >
          {FAMILLES.map((f) => (
            <option key={f.code} value={f.libelle}>
              {f.code} · {f.libelle}
            </option>
          ))}
        </select>
      </td>
      <td className="a-droite">
        <input
          className="saisie saisie-compacte a-droite"
          style={{ width: 60 }}
          inputMode="numeric"
          value={quantite}
          onChange={(e) => setQuantite(e.target.value.replace(/\D/g, ''))}
        />
      </td>
      <td className="a-droite">
        <input
          className="saisie saisie-compacte a-droite"
          style={{ width: 90 }}
          inputMode="decimal"
          value={prix}
          disabled={prixVerrouille}
          title={prixVerrouille ? 'Article vendu : le prix ne peut plus changer.' : undefined}
          onChange={(e) => setPrix(e.target.value)}
        />
      </td>
      <td className="a-droite sourdine" colSpan={2}>
        {prixVerrouille && `${vendus} vendu${vendus > 1 ? 's' : ''}`}
      </td>
      <td>
        <span className={`pastille pastille-${article.statut}`}>
          {article.statut === 'vendu' ? 'Vendu' : 'En dépôt'}
        </span>
      </td>
      <td className="a-droite" style={{ whiteSpace: 'nowrap' }}>
        <button
          type="button"
          className="btn btn-fantome"
          disabled={envoi || !modifie}
          onClick={() => void enregistrer()}
        >
          Enregistrer
        </button>
        <button
          type="button"
          className="btn btn-secondaire btn-icone"
          disabled={envoi || vendus > 0}
          title={vendus > 0 ? 'Déjà vendu — retirez-le du rayon plutôt.' : 'Retirer du contrat'}
          onClick={() => {
            if (window.confirm(`Retirer ${article.ref} du contrat ?`)) void onRetirer()
          }}
        >
          ×
        </button>
      </td>
    </tr>
  )
}

/** L'oubli qu'on rattrape : un article ajouté à un contrat déjà signé. */
function AjoutArticle({
  onAjouter,
}: {
  onAjouter: (article: Record<string, unknown>) => Promise<void>
}) {
  const [titre, setTitre] = useState('')
  const [categorie, setCategorie] = useState(FAMILLES[0]!.libelle)
  const [prix, setPrix] = useState('')
  const [quantite, setQuantite] = useState('1')
  const [envoi, setEnvoi] = useState(false)

  const centimes = centimesDepuisSaisie(prix)
  const valide = titre.trim().length > 0 && centimes !== null && centimes > 0

  return (
    <div className="ajout-article">
      <input
        className="saisie"
        style={{ flex: 2, minWidth: 180 }}
        placeholder="Désignation oubliée"
        value={titre}
        onChange={(e) => setTitre(e.target.value)}
      />
      <select
        className="saisie"
        style={{ flex: 1, minWidth: 140 }}
        value={categorie}
        onChange={(e) => setCategorie(e.target.value)}
      >
        {FAMILLES.map((f) => (
          <option key={f.code} value={f.libelle}>
            {f.code} · {f.libelle}
          </option>
        ))}
      </select>
      <input
        className="saisie"
        style={{ width: 80 }}
        inputMode="numeric"
        aria-label="Quantité"
        value={quantite}
        onChange={(e) => setQuantite(e.target.value.replace(/\D/g, ''))}
      />
      <input
        className="saisie"
        style={{ width: 110 }}
        inputMode="decimal"
        placeholder="Prix"
        value={prix}
        onChange={(e) => setPrix(e.target.value)}
      />
      <button
        type="button"
        className="btn btn-primaire"
        disabled={envoi || !valide}
        onClick={async () => {
          setEnvoi(true)
          await onAjouter({
            titre: titre.trim(),
            categorie,
            prixCentimes: centimes,
            quantite: Math.max(1, Number.parseInt(quantite, 10) || 1),
            volumineux: false,
          })
          setTitre('')
          setPrix('')
          setQuantite('1')
          setEnvoi(false)
        }}
      >
        + Ajouter au contrat
      </button>
    </div>
  )
}
